import React, { useState, useEffect, useMemo } from 'react';
import Wrapper from '../../components/Wrapper';
import {
  Paper,
  Typography,
  Box,
  IconButton,
  Button,
  Card,
  CardContent,
  Chip,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import SmsIcon from '@mui/icons-material/Sms';
import AddIcon from '@mui/icons-material/Add';
import BarChartIcon from '@mui/icons-material/BarChart';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import { greenTheme } from '../../helpers/style';

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const primary = greenTheme.primary;
const telegramColor = '#0088cc';
const whatsappColor = '#25D366';

let nextId = 1;

const TIME_FILTER_OPTIONS = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: 'all', label: 'All time' },
];

export default function SmsProvidingMain() {
  const navigate = useNavigate();
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [timeFilter, setTimeFilter] = useState('30');

  const getFilteredItems = () => {
    if (timeFilter === 'all') return items;
    const days = parseInt(timeFilter, 10);
    const cutoff = Date.now() - days * 86400000;
    return items.filter((it) => {
      const date = it.created_at ? new Date(it.created_at).getTime() : 0;
      return date >= cutoff;
    });
  };

  const filteredItems = getFilteredItems();

  const stats = useMemo(() => filteredItems.reduce(
    (acc, it) => {
      const ch = it.channel === 'telegram' ? 'telegram' : 'whatsapp';
      const count = it.phone_count ?? 0;
      if (it.status === 'sent') {
        acc[ch].sent += count;
      } else if (it.status === 'scheduled') {
        acc[ch].scheduled += count;
      } else {
        acc[ch].failed += count;
      }
      return acc;
    },
    { telegram: { sent: 0, scheduled: 0, failed: 0 }, whatsapp: { sent: 0, scheduled: 0, failed: 0 } }
  ), [filteredItems]);

  const barChartData = useMemo(
    () => ({
      labels: ['Telegram', 'WhatsApp'],
      datasets: [
        {
          label: 'Sent',
          data: [stats.telegram.sent, stats.whatsapp.sent],
          backgroundColor: [`${telegramColor}cc`, `${whatsappColor}cc`],
          borderColor: [telegramColor, whatsappColor],
          borderWidth: 2,
        },
        {
          label: 'Left to send',
          data: [stats.telegram.scheduled, stats.whatsapp.scheduled],
          backgroundColor: [`${telegramColor}66`, `${whatsappColor}66`],
          borderColor: [telegramColor, whatsappColor],
          borderWidth: 2,
        },
      ],
    }),
    [stats]
  );

  const doughnutData = useMemo(
    () => ({
      labels: ['Telegram', 'WhatsApp'],
      datasets: [
        {
          data: [stats.telegram.sent + stats.telegram.scheduled, stats.whatsapp.sent + stats.whatsapp.scheduled],
          backgroundColor: [telegramColor, whatsappColor],
          borderColor: ['#fff', '#fff'],
          borderWidth: 3,
          hoverOffset: 8,
        },
      ],
    }),
    [stats]
  );

  useEffect(() => {
    const newItem = location.state?.newBulkSend;
    if (newItem) {
      setItems((prev) => [{ ...newItem, id: nextId++ }, ...prev]);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.newBulkSend, location.pathname, navigate]);

  const statusColor = (status) => {
    if (status === 'sent') return 'success';
    if (status === 'failed') return 'error';
    if (status === 'scheduled') return 'info';
    return 'default';
  };

  const channelColor = (ch) => (ch === 'telegram' ? telegramColor : whatsappColor);

  return (
    <Wrapper>
      <div className="tableParent">
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3, borderBottom: '2px solid rgba(0, 0, 0, 0.08)', pb: 2.5 }}>
            <IconButton size="small" sx={{ color: primary, fontSize: 18 }} onClick={() => navigate(-1)} aria-label="Back">
              <ArrowBackIosNewIcon sx={{ fontSize: 20, fontWeight: 700 }} />
            </IconButton>
            <SmsIcon sx={{ color: primary, fontSize: 28, mr: 0.5 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, m: 0 }}>
              Bulk messaging
            </Typography>
          </Box>

          <Tabs
            value={tabValue}
            onChange={(_, v) => setTabValue(v)}
            sx={{
              mb: 2,
              minHeight: 42,
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
              '& .Mui-selected': { color: primary },
              '& .MuiTabs-indicator': { backgroundColor: primary },
            }}
          >
            <Tab label="Bulk sendings" value={0} />
            <Tab label="Graphs" value={1} icon={<BarChartIcon />} iconPosition="start" />
          </Tabs>

          <Paper sx={{ p: 0, boxShadow: 'none' }}>
            {tabValue === 0 && (
            <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: 'text.primary' }}>
                Bulk sendings
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  size="medium"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/short_function/sms_providing/create')}
                  sx={{
                    backgroundColor: primary,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': { backgroundColor: greenTheme.hover },
                  }}
                >
                  Add
                </Button>
              </Box>
            </Box>

            {items.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                No bulk sendings yet. Click &quot;Add&quot; to create one.
              </Typography>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
                {items.map((row) => (
                  <Card
                    key={row.id}
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      borderLeft: `4px solid ${channelColor(row.channel)}`,
                      transition: 'box-shadow 0.2s',
                      '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.08)' },
                    }}
                  >
                    <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 1.5 }}>
                        <Chip
                          label={row.channel === 'telegram' ? 'Telegram' : 'WhatsApp'}
                          size="small"
                          sx={{
                            bgcolor: `${channelColor(row.channel)}22`,
                            color: channelColor(row.channel),
                            fontWeight: 600,
                          }}
                        />
                        <Chip label={row.status} size="small" color={statusColor(row.status)} />
                        <Typography variant="caption" color="text.secondary">
                          {row.send_at
                            ? `Scheduled: ${new Date(row.send_at).toLocaleString()}`
                            : row.created_at
                              ? new Date(row.created_at).toLocaleString()
                              : '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                          {row.phone_count ?? 0} recipient{(row.phone_count ?? 0) !== 1 ? 's' : ''}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.5 }}>
                        {row.message || '—'}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}
            </>
            )}

            {tabValue === 1 && (
              <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Time range</InputLabel>
                    <Select
                      value={timeFilter}
                      label="Time range"
                      onChange={(e) => setTimeFilter(e.target.value)}
                      sx={{
                        borderRadius: 2,
                        '& .MuiSelect-select': { py: 1.25 },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                      }}
                    >
                      {TIME_FILTER_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      borderRadius: 3,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={700} sx={{ color: 'text.primary', mb: 2 }}>
                      Messages by provider
                    </Typography>
                    <Box sx={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Bar
                        data={barChartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: { position: 'top' },
                            tooltip: { mode: 'index', intersect: false },
                          },
                          scales: {
                            x: {
                              grid: { display: false },
                              ticks: { font: { size: 13 } },
                            },
                            y: {
                              beginAtZero: true,
                              grid: { color: 'rgba(0,0,0,0.06)' },
                              ticks: { stepSize: 1 },
                            },
                          },
                        }}
                      />
                    </Box>
                  </Paper>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      borderRadius: 3,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={700} sx={{ color: 'text.primary', mb: 2 }}>
                      Distribution by channel
                    </Typography>
                    <Box sx={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Doughnut
                        data={doughnutData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          cutout: '60%',
                          plugins: {
                            legend: { position: 'bottom' },
                            tooltip: { callbacks: { label: (ctx) => `${ctx.label}: ${ctx.raw} (${((ctx.raw / ctx.dataset.data.reduce((a, b) => a + b, 0)) * 100).toFixed(1)}%)` } },
                          },
                        }}
                      />
                    </Box>
                  </Paper>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mt: 3 }}>
                  <Card variant="outlined" sx={{ borderRadius: 2, borderLeft: `4px solid ${telegramColor}`, boxShadow: 'none' }}>
                    <CardContent sx={{ py: 2, px: 2.5 }}>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ color: telegramColor, mb: 1.5 }}>
                        Telegram
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 3 }}>
                        <Typography variant="body2" color="text.secondary">Sent: <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{stats.telegram.sent}</Box></Typography>
                        <Typography variant="body2" color="text.secondary">Left: <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{stats.telegram.scheduled}</Box></Typography>
                        {stats.telegram.failed > 0 && <Typography variant="body2" color="error">Failed: {stats.telegram.failed}</Typography>}
                      </Box>
                    </CardContent>
                  </Card>
                  <Card variant="outlined" sx={{ borderRadius: 2, borderLeft: `4px solid ${whatsappColor}`, boxShadow: 'none' }}>
                    <CardContent sx={{ py: 2, px: 2.5 }}>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ color: whatsappColor, mb: 1.5 }}>
                        WhatsApp
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 3 }}>
                        <Typography variant="body2" color="text.secondary">Sent: <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{stats.whatsapp.sent}</Box></Typography>
                        <Typography variant="body2" color="text.secondary">Left: <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{stats.whatsapp.scheduled}</Box></Typography>
                        {stats.whatsapp.failed > 0 && <Typography variant="body2" color="error">Failed: {stats.whatsapp.failed}</Typography>}
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              </Box>
            )}
          </Paper>
        </Paper>
      </div>
    </Wrapper>
  );
}
